import { LocalDataError } from '../types.js';
import { toWireStatements } from './opfsProtocol.js';
export class OpfsSqliteDriver {
    filename;
    worker;
    nextId = 1;
    pending = new Map();
    ready;
    closed = false;
    constructor(filename, worker) {
        this.filename = filename;
        this.worker = worker;
        this.worker.onmessage = (event) => this.handleMessage(event.data);
        this.worker.onerror = (event) => this.handleFatal(event);
    }
    /** Sends the one-time `init` request that opens the OPFS-backed database. */
    async ensureReady() {
        if (this.closed)
            throw new LocalDataError('OPFS_CLOSED', 'This local database has been closed');
        this.ready ??= this.call({ op: 'init', filename: this.filename }).then(() => undefined);
        return this.ready;
    }
    async query(statement) {
        await this.ensureReady();
        const [result] = await this.call({ op: 'query', statements: toWireStatements([statement]) });
        return result?.rows ?? [];
    }
    async execute(statement) {
        await this.ensureReady();
        const [result] = await this.call({ op: 'execute', statements: toWireStatements([statement]) });
        return result ?? { changes: 0, rows: [] };
    }
    async batch(statements) {
        if (statements.length === 0)
            return [];
        await this.ensureReady();
        return this.call({ op: 'batch', statements: toWireStatements(statements) });
    }
    /**
     * Erases the local database: closes it and deletes every file in the OPFS
     * pool. The driver is unusable afterwards. Callers decide whether unsynced
     * work may be lost - this does not check (see wipeLocalDatabase in the web
     * app, which does).
     */
    async wipe() {
        if (this.closed)
            throw new LocalDataError('OPFS_CLOSED', 'This local database has been closed');
        try {
            await this.call({ op: 'wipe' });
        }
        finally {
            this.closed = true;
            this.worker.terminate?.();
            for (const { reject } of this.pending.values()) {
                reject(new LocalDataError('OPFS_CLOSED', 'This local database has been wiped'));
            }
            this.pending.clear();
        }
    }
    async close() {
        if (this.closed)
            return;
        this.closed = true;
        try {
            if (this.ready) {
                await this.call({ op: 'close' });
            }
        }
        finally {
            this.worker.terminate?.();
            for (const { reject } of this.pending.values()) {
                reject(new LocalDataError('OPFS_CLOSED', 'This local database has been closed'));
            }
            this.pending.clear();
        }
    }
    call(request) {
        const id = this.nextId;
        this.nextId += 1;
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            this.worker.postMessage({ ...request, id });
        });
    }
    handleMessage(response) {
        const pending = this.pending.get(response.id);
        if (!pending)
            return; // A response for a call we no longer care about (e.g. after close()).
        this.pending.delete(response.id);
        if (response.ok) {
            pending.resolve(response.results);
        }
        else {
            pending.reject(new LocalDataError(response.code, response.message));
        }
    }
    /**
     * The worker crashed outright (not a normal error response) - fail
     * everything in flight. Duck-types the event instead of naming
     * `ErrorEvent` (a lib.dom type) so this file stays DOM-free; the real
     * `Worker.onerror` always passes an object with a string `message`.
     */
    handleFatal(event) {
        const message = event && typeof event === 'object' && typeof event.message === 'string'
            ? event.message
            : 'The local database worker crashed';
        const error = new LocalDataError('OPFS_WORKER_CRASHED', message);
        for (const { reject } of this.pending.values())
            reject(error);
        this.pending.clear();
    }
}
//# sourceMappingURL=opfs.js.map